import * as React from "react";
import type { ColumnConfigState } from "@racoon/ui";
import { Namespace, useTypedT } from "@racoon/i18n";
import { MswRouteListTable, MswRouteListTableHeader } from "./MswRouteListTable";
import { useSyncedScrollX } from "./use-synced-scroll-x";
import { RouteUnloadRow } from "./RouteUnloadRow";
import { RouteFactTimesRow } from "./RouteFactTimesRow";
import { SourceLegend } from "./ttn-source";
import { RouteStatusPill } from "./status-pills";
import { TripCascadeRow } from "./trip-cascade-row";
import { compareIds } from "~/shared/lib/compare-ids";
import { EMPTY_SELECTED_IDS, RouteActionsMenu, useRouteActions } from "./route-actions-menu";
import { usePickupLocationCard } from "~/features/pickup-location-modal/hooks/use-pickup-location-card";
import { useCustomerCardModal } from "~/features/customer-card/hooks/use-customer-card-modal";
import { useJobCard } from "~/pages/dashboard/orders/ui/use-job-card";
import { useRouteJobsModal } from "../../hooks/useRouteJobsModal";
import {
  getRouteJobsCount,
  getRoutesContainerCount,
  getRouteFactContainerCount,
  getRouteFactVolume,
  getRouteTotalVolume,
  getTripNumbers,
} from "../../utils/routeMetrics";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { ConnectProto } from "@racoon/api";
import type { TripTimes } from "../../hooks/ttn/useTtnDraft";

type Props = {
  routes: ConnectProto.Route[];
  expandedRouteId: bigint | null;
  onExpand: (routeId: bigint | null) => void;
  /** Every route open at once, ignoring `expandedRouteId`: the TTN PDF snapshot. */
  expandAll?: boolean;
  selectionResetToken?: bigint;
  onSelectionMapChange?: (selectionMap: Map<bigint, Set<bigint>>) => void;
  selectedDate?: Date;
  onRefetch?: () => void;
  focusedJobId?: bigint | null;
  scrollToFocusedJob?: boolean;
  onJobFocus?: (jobId: bigint) => void;
  showSelection?: boolean;
  /**
   * TTN page: the jobs table shows the TTN columns, the unload place edits its
   * draft, and the route itself is read-only — a change made here would bypass
   * the TTN approval.
   */
  ttn?: {
    /** The shift's organization; the unload picker must not fall back to another one. */
    orgId: bigint;
    onDestinationChange: (route: ConnectProto.Route, destination: ConnectProto.Location) => void;
    /**
     * While the TTN is being approved (an edit made then would be lost with the
     * draft), and while an approved one is not being edited.
     */
    disabled?: boolean;
    /** Each trip's start / finish as the TTN shows them, coloured by source. */
    tripTimes?: Map<bigint, TripTimes>;
  };
};

export const MswRouteCascade: React.FC<Props> = ({
  routes,
  expandedRouteId,
  onExpand,
  expandAll = false,
  selectionResetToken,
  onSelectionMapChange,
  selectedDate,
  onRefetch,
  focusedJobId,
  scrollToFocusedJob,
  onJobFocus,
  showSelection = true,
  ttn,
}) => {
  const t = useTypedT(Namespace.Routes);
  // Nothing planned reads as "-"; a fact of zero (every container empty) is a
  // real figure and is shown as such.
  const volumeFigure = (volume: number, keepZero = false) =>
    volume > 0 || keepZero ? Number(volume.toFixed(3)) : "-";
  const { openRouteJobs } = useRouteJobsModal();
  const { selectedCompanyId } = useSelectedCompany();
  const orgId = ttn?.orgId ?? BigInt(selectedCompanyId || 1);
  const readOnly = Boolean(ttn);

  // One card of each for the whole sheet: a table per route would otherwise
  // mount its own.
  const { openLocation, pickupLocationCard } = usePickupLocationCard();
  const { openSingle: openCustomerCard } = useCustomerCardModal({
    providerId: selectedCompanyId ?? undefined,
  });
  const { openJob } = useJobCard();
  const actions = useRouteActions({ routes, orgId, selectedDate, onRefetch, openRouteJobs });
  const {
    routeSelectedMap,
    isUpdatingRoute,
    handleRemoveSelectedJobs,
    handleRowSelectionChange,
    handleReorderJobs,
    clearAllSelection,
    handleOpenRouteCard,
  } = actions;

  const sortedRoutes = React.useMemo(
    () => [...routes].sort((a, b) => compareIds(a.id, b.id)),
    [routes],
  );
  const tripNumbers = React.useMemo(() => getTripNumbers(sortedRoutes), [sortedRoutes]);
  // One column layout for the header and every trip: resizing in the header resizes all.
  const [columnConfig, setColumnConfig] = React.useState<ColumnConfigState>();

  const listRef = React.useRef<HTMLDivElement>(null);
  // Re-aligns only when the trips themselves change, not on every refetch.
  useSyncedScrollX(listRef, sortedRoutes.map((route) => String(route.id)).join());

  React.useEffect(() => {
    if (!onSelectionMapChange) return;
    onSelectionMapChange(new Map(routeSelectedMap));
  }, [onSelectionMapChange, routeSelectedMap]);

  React.useEffect(() => {
    if (selectionResetToken == null) return;
    clearAllSelection();
  }, [clearAllSelection, selectionResetToken]);

  return (
    <div ref={listRef} className="flex flex-col gap-2">
      {ttn?.tripTimes && <SourceLegend className="mb-2" />}
      {/* One header over every trip, as in the mock; each trip's table hides its own. */}
      <div className="sticky top-0 z-30 bg-white">
        <MswRouteListTableHeader
          routes={sortedRoutes}
          selection={routeSelectedMap}
          onSelectionChange={handleRowSelectionChange}
          showSelection={showSelection}
          withRemove={!readOnly}
          variant={ttn ? "ttn" : "default"}
          columnConfig={columnConfig}
          onColumnConfigChange={setColumnConfig}
        />
      </div>
      {sortedRoutes.map((route, index) => {
        const isExpanded =
          expandAll || (expandedRouteId != null && String(expandedRouteId) === String(route.id));

        const totalVolume = getRouteTotalVolume(route);
        // The TTN shows plan → fact for containers and volume: they part as soon
        // as a job's containers or volume are corrected.
        const factVolume = ttn ? getRouteFactVolume(route) : undefined;
        const routeJobsCount = getRouteJobsCount(route);
        const routeContCount = getRoutesContainerCount([route]);
        const factContCount = ttn ? getRouteFactContainerCount(route) : undefined;
        // Get selected job IDs for this route
        const selectedJobIds = routeSelectedMap.get(route.id) ?? EMPTY_SELECTED_IDS;

        return (
          <section key={`${route.id}-${index}`} className="">
            <TripCascadeRow
              routeId={route.id}
              tripNumber={tripNumbers.get(route.id)}
              expanded={isExpanded}
              onToggle={() => onExpand(isExpanded ? null : route.id)}
              // The route card edits the route directly, past the TTN draft,
              // so the read-only TTN tab offers no way into it.
              onOpenRoute={readOnly ? undefined : () => handleOpenRouteCard(route.id)}
              status={<RouteStatusPill route={route} />}
              summary={
                <>
                  <span>
                    {routeJobsCount} {t("jobsCountShort")}
                  </span>
                  <span>
                    {factContCount !== undefined
                      ? `${routeContCount} → ${factContCount}`
                      : routeContCount}{" "}
                    {t("containers")}
                  </span>
                  <span>
                    {factVolume !== undefined
                      ? t("volumeCubicMeters", {
                          volume: `${volumeFigure(totalVolume)} → ${volumeFigure(factVolume, true)}`,
                        })
                      : t("volumeCubicMeters", { volume: volumeFigure(totalVolume) })}
                  </span>
                </>
              }
              actions={
                <RouteActionsMenu
                  route={route}
                  selectedJobIds={selectedJobIds}
                  readOnly={readOnly}
                  actions={actions}
                />
              }
            >
              <RouteFactTimesRow route={route} times={ttn?.tripTimes?.get(route.id)} />
              <div className="min-h-0 overflow-auto">
                <MswRouteListTable
                  routeStatus={route.status}
                  jobs={route.jobs.map((job) =>
                    job.driverOnShift ? job : { ...job, driverOnShift: route.driverOnShift },
                  )}
                  selectedIds={selectedJobIds}
                  focusedJobId={focusedJobId}
                  scrollToFocusedJob={scrollToFocusedJob}
                  onJobFocus={onJobFocus}
                  onSelectionChange={(newSelectedIds) =>
                    handleRowSelectionChange(route.id, newSelectedIds)
                  }
                  onRemoveJob={
                    readOnly
                      ? undefined
                      : (jobId) => handleRemoveSelectedJobs(route, new Set([jobId]))
                  }
                  isRemoving={isUpdatingRoute}
                  onJobsReorder={(reorderedJobIds) => handleReorderJobs(route.id, reorderedJobIds)}
                  onLocationClick={openLocation}
                  onCustomerClick={openCustomerCard}
                  onJobClick={openJob}
                  showSelection={showSelection}
                  variant={ttn ? "ttn" : "default"}
                  headerMode="hidden"
                  columnConfig={columnConfig}
                />
              </div>
              <RouteUnloadRow
                route={route}
                orgId={orgId}
                onRefetch={onRefetch}
                onDestinationChange={ttn?.onDestinationChange}
                disabled={ttn?.disabled}
                showFactVolume={Boolean(ttn)}
              />
            </TripCascadeRow>
          </section>
        );
      })}
      {actions.modals}
      {pickupLocationCard}
    </div>
  );
};

export default MswRouteCascade;
